<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue'
import { useStore } from '@/store'
import appAxios from '@/libs/app-axios'
import { ErrorPopup, LoadingPopup } from '@/libs/utils'

/* ================= STORE ================= */
const store = useStore()

const accessLevel = computed(() => store.state.auth?.accessLevel)
const barColor = computed(() => store.state.barColor)

const filters = reactive({
  date_from: null,
  date_to: null,
})
/* ================= TABLE HEADERS ================= */
const headers = computed(() => {
  const headersArray = [
    { text: 'Date', value: 'date' },
    { text: 'Time In', value: 'time_in' },
    { text: 'Time Out', value: 'time_out' },
    { text: 'OT In', value: 'ot_in' },
    { text: 'OT Out', value: 'ot_out' },
    { text: 'OT Reason', value: 'ot_reason' },
    { text: 'Meal Allowance', value: 'has_meal_allowance', class: 'text-center', width: '120px' },
  ]
  if (accessLevel.value >= 5) {
    headersArray.unshift({ text: 'Employee', value: 'name' })
    headersArray.push({ text: 'Actions', value: 'actions', sortable: false })
  }
  return headersArray
})

/* ================= STATE ================= */
const tableState = reactive({
  items: [],
  isLoading: false,
})

const users = ref([])
const selectedEmployee = ref(null)

/* ================= FETCH USERS ================= */
const fetchUsers = async () => {
  try {
    const { data } = await appAxios().get('/v3/users', {
      params: {
        accessLevel: 1,
      },
    })
    users.value = data
  } catch (e) {
    console.log('Failed loading users')
  }
}

/* ================= FETCH ATTENDANCE ================= */
const fetchAttendanceSheets = async () => {
  try {
    tableState.isLoading = true

    const { data } = await appAxios().get('/v3/attendance-sheets', {
      params: {
        employeeId: selectedEmployee.value,
        dateFrom: filters.date_from,
        dateTo: filters.date_to,
      },
    })

    tableState.items = data || []
  } catch (e) {
    console.log(e)
    tableState.items = []
  } finally {
    tableState.isLoading = false
  }
}

const deleteAttendance = async (item) => {
  try {
    const confirmationText = 'DELETE'
    const userInput = prompt(
      `This action is irreversible!\n\nType "${confirmationText}" to confirm deletion of the attendance record on ${item.date}.`
    )

    if (userInput !== confirmationText) {
      alert('Deletion cancelled. Confirmation text did not match.')
      return
    }
    LoadingPopup.fire({
      title: 'Deleting attendance record...',
    })
    await appAxios().delete(`/v3/attendance-sheets/${item.id}`)
    fetchAttendanceSheets()
    alert('Record deleted successfully.')
    LoadingPopup.close()
  } catch (e) {
    LoadingPopup.close()
    console.log('Delete failed', e)
    ErrorPopup.fire('Failed to delete the record.')
  }
}

/* ================= DOWNLOAD EXCEL ================= */
const downloadCSV = async () => {
  try {
    const response = await appAxios().get('/v3/attendance-sheets/download-csv', {
      params: {
        employee_id: selectedEmployee.value,
        date_from: filters.date_from,
        date_to: filters.date_to,
      },
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'attendance.csv')
    document.body.appendChild(link)
    link.click()
  } catch (e) {
    console.log('Download failed')
  }
}

/* ================= INIT ================= */
onMounted(() => {
  fetchUsers()
  fetchAttendanceSheets()
})

/* ================= EXPOSE ================= */
defineExpose({
  fetchAttendanceSheets,
})
</script>

<template>
  <div class="flex flex-column" style="gap: 16px;">

    <!-- ================= FILTER ROW ================= -->
    <v-card v-if="accessLevel >= 5" outlined dense>
      <v-card-text class="d-flex align-center" style="gap: 16px; flex-wrap: wrap;">

        <!-- Employee Filter -->
        <v-select hide-details v-model="selectedEmployee" :items="users"
          :item-text="(item) => `${item.employee.id} - ${item.employee.name}`" item-value="employee_id" label="Employee"
          clearable dense outlined style="max-width: 280px" />

        <!-- Date From -->
        <v-text-field hide-details v-model="filters.date_from" type="date" label="Date From" dense outlined
          style="max-width: 200px" />

        <!-- Date To -->
        <v-text-field hide-details v-model="filters.date_to" type="date" label="Date To" dense outlined
          style="max-width: 200px" />

        <!-- Apply Button -->
        <v-btn color="primary" @click="fetchAttendanceSheets">
          Apply
        </v-btn>

        <!-- Download Excel Button -->
        <v-btn color="success" @click="downloadCSV">
          <v-icon left>mdi-file-excel</v-icon>
          Download CSV
        </v-btn>

      </v-card-text>
    </v-card>

    <!-- ================= TABLE ================= -->
    <v-data-table :headers="headers" :items="tableState.items" :loading="tableState.isLoading" dense>
      <template #item.has_meal_allowance="{ value }">
        <div class="text-center">
          <v-icon color="success" v-if="value">
            mdi-check
          </v-icon>
        </div>
      </template>

      <!-- DELETE BUTTON -->
      <template v-if="accessLevel >= 5" #item.actions="{ item }">
        <v-btn icon color="error" @click="deleteAttendance(item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

  </div>
</template>

<script>
export default {
  name: 'AttendanceSheetIndex',
}
</script>
